import type { SelectOption } from '@tix/ds/components/atoms/form-input/types.ts'

import { getEnv } from '@/helpers/environment'

import { formatCountry, regionLabelKeys, regions } from './countriesAndRegions'
import { filterEntries, mapEntries } from './dictHelpers'

export type IdentityFormFieldType =
  'text' | 'email' | 'tel' | 'phone-number' | 'select' | 'checkbox' | 'country' | 'options' | 'password'

export interface IdentityFormField {
  key: string
  type: IdentityFormFieldType
  label?: string
  labelHtml?: string
  description?: string
  required: boolean
  maxlength?: number
  minlength?: number
  disabled?: boolean
  readOnly?: boolean
  autocomplete?: string
  pattern?: string
  options?: SelectOption[]
  enum?: string[]
}

export interface CustomIdentityField {
  title: string
  description?: string
  type: 'string' | 'boolean'
  enum?: string[]
  format?: string
  editor?: string
  required?: boolean
  default?: string | boolean
}

export type IdentityFormDataValue = string | boolean
export type IdentityFormData = Record<string, IdentityFormDataValue>
export type PatchableIdentityFormData = Record<string, IdentityFormDataValue | null>

export type IdentityUserPayload = {
  first_name: string
  last_name: string
  email: string
  additional_info: Record<string, IdentityFormDataValue>
}

const IDENTITY_LABELS = {
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email address',
  state: 'State',
  province: 'Province',
} as const

export function configIdentityFields(): Record<string, CustomIdentityField> {
  const fields = getEnv().config.config?.custom_identity_fields
  return (fields as Record<string, CustomIdentityField>) ?? {}
}

export function builtInFields(): Record<string, IdentityFormField> {
  const t = IDENTITY_LABELS

  const email: Omit<IdentityFormField, 'key'> = {
    label: t.email,
    type: 'email',
    required: true,
    autocomplete: 'email',
    maxlength: 254,
  }

  const result: Record<string, Omit<IdentityFormField, 'key'>> = {
    first_name: {
      label: t.firstName,
      type: 'text',
      required: true,
      maxlength: 50,
      autocomplete: 'given-name',
    },
    last_name: {
      label: t.lastName,
      type: 'text',
      required: true,
      maxlength: 50,
      autocomplete: 'family-name',
    },
    email,
    email_confirm: {
      ...email,
      label: 'Confirm email address',
    },
    password: {
      label: 'Password',
      type: 'password',
      required: true,
      maxlength: 254,
    },
    password_confirm: {
      label: 'Confirm password',
      type: 'password',
      required: true,
      maxlength: 254,
    },
  }

  return mapEntries(result, (value, key) => ({ ...value, key }))
}

export function customFieldsDefaults(): IdentityFormData {
  return mapEntries(configIdentityFields(), (field) => field.default ?? '')
}

const fallbackAutocompleteAttrs: Record<string, string> = {
  address: 'street-address',
}

export function customFields(answers: IdentityFormData): Record<string, IdentityFormField> {
  return mapEntries(configIdentityFields(), (field, key) => {
    const attributes = customFieldAttributes(field, answers)
    return {
      key,
      label: field.title,
      labelHtml: field.title,
      required: Boolean(field.required),
      maxlength: 1000,
      ...attributes,
      type: attributes.type ?? 'text',
      autocomplete: attributes.autocomplete ?? fallbackAutocompleteAttrs[key],
    }
  })
}

function customFieldAttributes(field: CustomIdentityField, answers: IdentityFormData): Partial<IdentityFormField> {
  const zipCodeFormat: Partial<IdentityFormField> = {
    type: 'text',
    autocomplete: 'postal-code',
    pattern: '[A-Za-z0-9]+(\\u0020|-)?[A-Za-z0-9]*',
    minlength: 4,
    maxlength: 10,
  }

  const formats: Record<string, Partial<IdentityFormField>> = {
    email: { type: 'email' },
    phone: { type: 'tel', maxlength: 50, autocomplete: 'tel' },
    zip_code: zipCodeFormat,
    post_code: zipCodeFormat,
    country: {
      type: 'country',
      autocomplete: 'country',
    },
    state: stateField(answers),
  }

  if (field.enum) {
    return {
      type: 'select',
      options: field.enum.map((value) => ({ value, label: value })),
    }
  }

  if (field.type === 'boolean') {
    return {
      type: 'checkbox',
      description: field.description,
    }
  }

  if (field.format && formats[field.format]) {
    return formats[field.format]
  }

  return { type: 'text' }
}

function stateField(answers: IdentityFormData): Partial<IdentityFormField> {
  const country =
    formatCountry((answers.country || customFieldsDefaults().country) as string) ??
    ((answers.country || customFieldsDefaults().country) as string)?.toLowerCase()
  const labelKey = regionLabelKeys[country] || 'state'
  const required = regions[country] != null
  return {
    label: IDENTITY_LABELS[labelKey],
    type: 'select',
    options: regions[country],
    required,
    disabled: !required,
  }
}

export function getIdentityPayload(data: PatchableIdentityFormData): IdentityUserPayload {
  return {
    first_name: data.first_name as string,
    last_name: data.last_name as string,
    email: data.email as string,
    additional_info: sanitizeIdentityMeta(data),
  }
}

export function sanitizeIdentityMeta(data: PatchableIdentityFormData): IdentityUserPayload['additional_info'] {
  const builtIn = new Set(Object.keys(builtInFields()))
  return filterEntries(data, (_, key) => !builtIn.has(key)) as IdentityUserPayload['additional_info']
}

export function isGiftAidFieldKey(key: string): boolean {
  return key === 'gift_aid_eligible'
}
